// Interest.jsx — shared sliding-ritual primitives (slug-keyed interest counts).
// Loaded on both index.html and venue.html so every event surface (homepage
// EventCard, Map popup, venue page row chevron + TICKETS button) can fire
// the ritual against a single in-memory cache + worker endpoint.
//
// Module surface:
//   markInterested(slug)          — first-time-mark gate (localStorage) + POST
//   fetchInterestCounts(slugs)    — batch read for hydration (60s localStorage TTL)
//   useInterestCount(slug)        — React hook for live count display
//   SlidingChip({ slug, darkMode }) — count display chip (threshold 3 / 10)
//
// Internals (do NOT use from other files):
//   _interestCache             — Map<slug, count>
//   _interestListeners         — Array of () => void
//   postInterest(slug)         — optimistic increment + POST + reconcile
//
// Babel-standalone shares global scope across <script type="text/babel">
// blocks, so top-level decls here become globals once this file loads.
// Must load BEFORE any file that calls these (Events.jsx, VenueEventList.jsx).
//
// Threshold rules + chip text live in lib/interest-chip.js (pure helpers,
// vitest-unit-tested). Exposed via window.slidingChipText / window.shouldRenderChip
// when that module loads via <script type="module"> in the host HTML.

/* ── Interest Counts (social proof) ── */
const _interestCache = new Map();
const _interestListeners = [];

// localStorage TTL for batch-read cache (60 seconds). Per-slug entries are
// `{count, ts}` keyed under `onsumn-interest-cache:{slug}`. Used by
// hydrateVisibleSlugs() to skip the network when fresh values exist.
const _INTEREST_CACHE_TTL_MS = 60_000;
const _INTEREST_CACHE_KEY_PREFIX = 'onsumn-interest-cache:';

// Hard ceiling on retained entries. A browse-heavy session can touch every
// event in the feed (1400+ and rising), and before 2026-07-28 nothing ever
// removed one: reads treated an expired entry as a miss and returned null,
// leaving the row on disk forever. That is a lifecycle with a ripen and a
// renew but no EXPIRE — the exact gap feedback_data_lifecycle exists to
// prevent. It also inflated the aggregate count the WHAT THIS BROWSER KNOWS
// panel reports, so the disclosure over-stated what was actually being used.
const _INTEREST_CACHE_MAX_ENTRIES = 400;
// Scanning every key on every write would be O(n) per card render, so the
// sweep is throttled. Expired singles are still evicted eagerly on read.
const _INTEREST_SWEEP_INTERVAL_MS = 30_000;
let _lastInterestSweep = 0;

function _interestCacheKeys() {
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.indexOf(_INTEREST_CACHE_KEY_PREFIX) === 0) out.push(k);
  }
  return out;
}

/**
 * Drop expired entries, then trim oldest-first if still over the ceiling.
 * `force` bypasses the throttle (used on a quota failure, where the whole
 * point is to make room before retrying).
 */
function _sweepInterestCache(force) {
  const now = Date.now();
  if (!force && now - _lastInterestSweep < _INTEREST_SWEEP_INTERVAL_MS) return 0;
  _lastInterestSweep = now;
  let removed = 0;
  try {
    const live = [];
    for (const key of _interestCacheKeys()) {
      let ts = 0;
      try {
        const parsed = JSON.parse(localStorage.getItem(key));
        ts = parsed && typeof parsed.ts === 'number' ? parsed.ts : 0;
      } catch { ts = 0; } // unparseable — treat as ancient and evict
      if (!ts || now - ts > _INTEREST_CACHE_TTL_MS) {
        localStorage.removeItem(key);
        removed++;
      } else {
        live.push({ key, ts });
      }
    }
    if (live.length > _INTEREST_CACHE_MAX_ENTRIES) {
      live.sort((a, b) => a.ts - b.ts); // oldest first
      for (const { key } of live.slice(0, live.length - _INTEREST_CACHE_MAX_ENTRIES)) {
        localStorage.removeItem(key);
        removed++;
      }
    }
  } catch { /* storage unavailable — nothing to sweep */ }
  return removed;
}

function _readCachedCount(slug) {
  try {
    const key = _INTEREST_CACHE_KEY_PREFIX + slug;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    // Parse in its own try: a throw here must still EVICT. Letting it fall to
    // the outer catch would return null and leave the corrupt row on disk
    // forever — a permanent, unreadable, undeletable entry.
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
    if (!parsed || typeof parsed.count !== 'number' || typeof parsed.ts !== 'number') {
      localStorage.removeItem(key); // malformed: it can never become valid
      return null;
    }
    if (Date.now() - parsed.ts > _INTEREST_CACHE_TTL_MS) {
      // EXPIRE, don't just ignore. Returning null while leaving the row on
      // disk is what let this cache grow without bound.
      localStorage.removeItem(key);
      return null;
    }
    return parsed.count;
  } catch { return null; }
}

function _writeCachedCount(slug, count) {
  const key = _INTEREST_CACHE_KEY_PREFIX + slug;
  const payload = JSON.stringify({ count, ts: Date.now() });
  try {
    localStorage.setItem(key, payload);
  } catch {
    // Quota. Previously swallowed, which meant a full store silently stopped
    // caching forever. Sweep and retry once; if it still fails, give up
    // quietly — a cache miss is a network call, not a broken UI.
    try {
      _sweepInterestCache(true);
      localStorage.setItem(key, payload);
    } catch { /* still full — degrade to no caching */ }
  }
  _sweepInterestCache(false);
}

// Defensive inline chunker used only when window.chunkSlugs is missing
// (lib/interest-chip.js failed to load — CDN flake, CSP regression, or the
// host page forgot to include the <script src>). Mirrors the pure helper's
// 50-cap behavior so Map view's 75+ pins never get blasted as a single
// over-cap fetch (worker returns 400 → cache permanent-zero-fills).
function _inlineChunkSlugs(slugs, size) {
  if (!Array.isArray(slugs) || slugs.length === 0) return [];
  const n = (typeof size === 'number' && size > 0) ? size : 50;
  const out = [];
  for (let i = 0; i < slugs.length; i += n) {
    out.push(slugs.slice(i, i + n));
  }
  return out;
}

/* The ONE reader for onsumn-interests moved to lib/interest-read.js (pique
   Phase 5) — the gap module needs it on non-React surfaces; one parser,
   one file (G3a doctrine). Loaded before this file per page script order.
   markInterested BAILS (false, no write, no POST) if the reader is absent
   rather than treating "can't read" as "unmarked" — that fallback would
   re-push an existing slug AND double-increment the public count. */

/* Shared dedupe-at-call-time helper for any "mark me interested in this slug"
   gesture (card-tap, flame-tap, etc.). Re-reads localStorage on every call so
   two gestures on the same mounted card can't double-count. Returns true if
   this was a first-time mark (caller may show ritual), false if a no-op. */
function markInterested(slug) {
  if (!slug) return false;
  if (typeof window === 'undefined' || typeof window.readInterested !== 'function') return false;
  const stored = window.readInterested();
  if (stored.includes(slug)) return false;
  stored.push(slug);
  localStorage.setItem('onsumn-interests', JSON.stringify(stored));
  postInterest(slug);
  /* Account-lite write-through. Deliberately AFTER postInterest and
     deliberately a separate call: the public counter bump is the GESTURE's
     job and happens exactly once here, while the profile push is storage
     only. T6 in both directions — this line adds no increment, and the sync
     layer never calls postInterest, so a mark that arrives by hydration on a
     second device can never replay this bump. No-ops when unattached. */
  if (window.OnSumnAccount) window.OnSumnAccount.pushField('marks');
  if (navigator.vibrate) navigator.vibrate(30);
  return true;
}

// Batch-read with 60s localStorage TTL. Splits the input into:
//   - cache-hits   → seed _interestCache + skip network
//   - cache-misses → chunked GET /api/interest?slugs=a,b,c (≤50 per chunk
//                    — worker returns 400 above that)
// Then notifies listeners once at the end so consumers re-render with both.
// chunkSlugs() lives in lib/interest-chip.js so vitest can exercise the
// boundary cases without booting React.
async function fetchInterestCounts(slugs) {
  if (!slugs || !slugs.length) return;
  const needsNetwork = [];
  slugs.forEach(s => {
    const cached = _readCachedCount(s);
    if (cached != null) {
      _interestCache.set(s, cached);
    } else if (!_interestCache.has(s)) {
      needsNetwork.push(s);
    }
  });

  if (needsNetwork.length) {
    // Chunk via the shared pure helper so the boundary rule has one home.
    // Promise.all so chunks fire in parallel — Map view with 75 pins gets
    // both chunks back roughly together rather than 2× the latency.
    //
    // If lib/interest-chip.js failed to load (CDN flake, CSP regression),
    // inline-chunk at 50 so we never blast all slugs in a single fetch and
    // hit the worker's 400-above-50 cap. A single chunk of all slugs would
    // permanent-zero-fill the cache for the whole page.
    const chunks = (typeof window !== 'undefined' && typeof window.chunkSlugs === 'function')
      ? window.chunkSlugs(needsNetwork)
      : _inlineChunkSlugs(needsNetwork, 50);
    let warnedThisCall = false;
    await Promise.all(chunks.map(async (chunk) => {
      try {
        const res = await fetch(`/api/interest?slugs=${chunk.join(',')}`);
        if (res.ok) {
          const data = await res.json();
          Object.entries(data).forEach(([slug, count]) => {
            _interestCache.set(slug, count);
            _writeCachedCount(slug, count);
          });
          return;
        }
        // Non-2xx (e.g. 400 if a chunk somehow exceeds the cap, 5xx, 429).
        // Log once per fetchInterestCounts call, zero-fill so the TTL guard
        // suppresses immediate retries until the cache window expires.
        if (!warnedThisCall) {
          // eslint-disable-next-line no-console
          console.warn('[onsumn:interest] batch read failed', res.status);
          warnedThisCall = true;
        }
        chunk.forEach(slug => {
          if (!_interestCache.has(slug)) _interestCache.set(slug, 0);
          _writeCachedCount(slug, 0);
        });
      } catch (_e) {
        // Offline / network error — keep whatever cache has, do NOT
        // zero-fill (next call may succeed and we want a real count).
      }
    }));
  }
  _interestListeners.forEach(fn => fn());
}

async function postInterest(slug) {
  const current = _interestCache.get(slug) || 0;
  _interestCache.set(slug, current + 1);
  _interestListeners.forEach(fn => fn());
  try {
    const res = await fetch(`/api/interest/${slug}`, { method: 'POST' });
    if (res.ok) {
      const data = await res.json();
      _interestCache.set(slug, data.count);
      _writeCachedCount(slug, data.count);
      _interestListeners.forEach(fn => fn());
    }
  } catch { /* keep optimistic */ }
}

function useInterestCount(slug) {
  const [count, setCount] = React.useState(_interestCache.get(slug) || 0);
  React.useEffect(() => {
    const listener = () => setCount(_interestCache.get(slug) || 0);
    _interestListeners.push(listener);
    return () => {
      const idx = _interestListeners.indexOf(listener);
      if (idx >= 0) _interestListeners.splice(idx, 1);
    };
  }, [slug]);
  return count;
}

// Hook: hydrate counts for a list of visible slugs on mount. Components
// pass their currently-rendered slugs in and SlidingChip will pick up the
// fresh values via useInterestCount. Re-runs when the slug list reference
// changes — callers are responsible for stable references (useMemo).
function useHydrateInterest(slugs) {
  React.useEffect(() => {
    if (!slugs || !slugs.length) return;
    // De-dupe + drop empties before hitting the network.
    const uniq = Array.from(new Set(slugs.filter(Boolean)));
    if (uniq.length) fetchInterestCounts(uniq);
  }, [slugs]);
}

/* SlidingChip — visible social-proof count chip.
 *
 * Threshold rules (lib/interest-chip.js):
 *   count < 3      → render nothing
 *   3 ≤ count ≤ 9  → "X sliding"
 *   count ≥ 10     → "🔥 X sliding"
 *
 * ARIA: role="status", aria-label="{count} people sliding". Live region so
 * a screen reader announces the change when the count crosses 3 or 10 while
 * the user is on the page. Light + dark mode parity via OKLCH tokens already
 * present in colors_and_type.css.
 *
 * `count` prop overrides the hook lookup — Map popup hands in the value it
 * already has so we don't double-subscribe. Default path subscribes via slug.
 */
function SlidingChip({ slug, count, darkMode }) {
  const hookCount = useInterestCount(slug);
  const effective = (typeof count === 'number') ? count : hookCount;
  // window.slidingChipText (from lib/interest-chip.js) is the source of truth
  // for the threshold + format string so the unit-tested rules can't drift.
  const text = (typeof window !== 'undefined' && typeof window.slidingChipText === 'function')
    ? window.slidingChipText(effective)
    : null;
  if (text == null) return null;

  // Two visual tiers (Mav behavioral — Phase B queue item #9):
  //
  //   LOW   (3 ≤ count < 10): text-only, quiet, doesn't crowd the row.
  //   FIRE  (count ≥ 10):     amber-on-dark pill, 10px caps. Real
  //                           social-proof signal — hot cards earn
  //                           visual weight, not just a fire emoji.
  //
  // Threshold lookup goes through the same window helper so SSR
  // sliding-chip (worker/preview.js) and the grid-card chip share
  // one tested boundary. Reduced-motion honored via the
  // `.onsumn-sliding-chip` keyframe override.
  const fireThreshold = (typeof window !== 'undefined' && typeof window.SLIDING_CHIP_FIRE_THRESHOLD === 'number')
    ? window.SLIDING_CHIP_FIRE_THRESHOLD
    : 10;
  const isFire = effective >= fireThreshold;

  // Lux spec (docs/design/sliding-share-visual-a11y.md) — LOW tier:
  //   - 12px / 13px line-height, weight 500, no background
  //   - dark: oklch(0.78 0.06 70)  — warm cream  ≥4.5:1 on dark cards
  //   - light: oklch(0.34 0.04 250) — deep fog blue ≥4.5:1 on warm cream
  //
  // FIRE tier (this commit) — pill treatment:
  //   - 10px caps, weight 700, 0.1em tracking
  //   - dark : amber surface, ink-black text → contrast ratio 7.2:1
  //   - light: amber surface, ink-black text → 7.2:1 (same — amber
  //     reads as ink on both surfaces; deliberate, ties to brand)
  //   - "X SLIDING" uppercase; the emoji prefix from slidingChipText
  //     gives the fire glyph for HCM-stripped contexts (color-
  //     independence per Iris a11y rule).
  if (isFire) {
    // Render the count + " SLIDING" rather than the raw helper string
    // so the pill stays compact (the helper emits "🔥 12 sliding" in
    // sentence case; pill format is "🔥 12 SLIDING"). We re-derive
    // from `effective` rather than uppercase the string so the fire
    // emoji isn't accidentally mangled by .toUpperCase on certain
    // platforms.
    const pillLabel = '\u{1F525} ' + effective + ' SLIDING';
    return (
      <span
        role="status"
        aria-label={`${effective} people sliding — hot`}
        className="onsumn-sliding-chip"
        style={{
          display: 'inline-flex', alignItems: 'center',
          fontFamily: 'var(--font-body)',
          fontSize: 10, fontWeight: 700,
          lineHeight: 1,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          // Amber-on-ink keeps the pill brand-on without competing
          // with the international-orange CTAs. Border drops in HCM
          // (forced-colors strips background) — declare a 1px outline
          // in the same color so the pill still reads as a chip.
          background: 'var(--amber-gold, #D57800)',
          color: '#0A0A0A',
          padding: '3px 8px',
          borderRadius: 100,
          border: '1px solid transparent',
          whiteSpace: 'nowrap',
        }}
      >{pillLabel}</span>
    );
  }

  return (
    <span
      role="status"
      aria-label={`${effective} people sliding`}
      // className triggers the 200ms fade-in keyframe defined in the
      // injected stylesheet. Reduced-motion is honored there.
      className="onsumn-sliding-chip"
      style={{
        display: 'inline-flex', alignItems: 'center',
        fontFamily: 'var(--font-body)',
        fontSize: 12, fontWeight: 500,
        lineHeight: '13px',
        letterSpacing: '0.01em',
        color: darkMode ? 'oklch(0.78 0.06 70)' : 'oklch(0.34 0.04 250)',
        background: 'none',
        padding: 0,
        border: 'none',
        whiteSpace: 'nowrap',
      }}
    >{text}</span>
  );
}

/* ShareIcon — 16x16 stroked square-with-up-arrow (iOS-style).
 * Visual-only — wrap in a <button> with the 44x44 hit area + aria-label. */
function ShareIcon({ darkMode }) {
  const stroke = darkMode ? 'rgba(255,255,255,0.78)' : 'rgba(20,20,20,0.78)';
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24"
      fill="none" stroke={stroke}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
    >
      {/* Box (open top) */}
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      {/* Up arrow */}
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Inject the sliding-ritual stylesheet once. Inline styles can't express
// :focus-visible, :active, keyframes, or @media (prefers-reduced-motion) —
// these all need a real stylesheet. Single style block, keyed off
// `.onsumn-share-btn` and `.onsumn-sliding-chip` + data-theme.
(function injectSlidingRitualStyleOnce() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('onsumn-share-btn-focus-style')) return;
  var style = document.createElement('style');
  style.id = 'onsumn-share-btn-focus-style';
  style.textContent =
    /* ── Sliding chip fade-in (Lux spec) ────────────────────────────── */
    // 200ms opacity 0→1 on mount. No translate, no scale (Lux explicit:
    // "no visual noise on dense cards"). Reduced-motion skips entirely.
    '@keyframes onsumnChipFadeIn{from{opacity:0;}to{opacity:1;}}' +
    '.onsumn-sliding-chip{' +
      'animation:onsumnChipFadeIn 200ms ease-out;' +
    '}' +

    /* ── Share-icon button: focus + press (Lux + Iris specs) ────────── */
    '.onsumn-share-btn{' +
      'outline:none;' +
      // Lux spec: 50ms press-scale transition. Reduced-motion overrides
      // below to no-op.
      'transition:transform 50ms ease-out;' +
    '}' +
    '.onsumn-share-btn:active{' +
      // Lux spec: scale(0.95) press state.
      'transform:scale(0.95);' +
    '}' +
    // Default ring (dark mode) — warm cream, matches the chip text token.
    '.onsumn-share-btn:focus-visible{' +
      'outline:2px solid oklch(0.78 0.06 70);' +
      'outline-offset:2px;' +
      'border-radius:8px;' +
    '}' +
    // Light theme — deep fog-blue ink, ≥3:1 against warm-cream card bg.
    ':root[data-theme="light"] .onsumn-share-btn:focus-visible{' +
      'outline:2px solid oklch(0.34 0.04 250);' +
      'outline-offset:2px;' +
    '}' +

    /* ── Reduced-motion: instant chip, no press-scale ──────────────── */
    '@media (prefers-reduced-motion: reduce){' +
      '.onsumn-sliding-chip{animation:none;opacity:1;}' +
      '.onsumn-share-btn{transition:none !important;}' +
      '.onsumn-share-btn:active{transform:none !important;}' +
    '}' +

    /* ── HCM: FIRE pill keeps its shape when system strips backgrounds.
     * Browser drops `background` under forced-colors; without an
     * outline the pill collapses to bare text and the "this is hot"
     * signal disappears. Re-paint with the system `Highlight` color
     * + `HighlightText` so the chip still reads as a chip. The fire
     * emoji prefix carries the semantic signal independent of color
     * (Iris a11y rule on color-independence). */
    '@media (forced-colors:active){' +
      '.onsumn-sliding-chip[role="status"][style*="--amber-gold"],' +
      '.onsumn-sliding-chip[aria-label$="hot"]{' +
        'background:Highlight !important;' +
        'color:HighlightText !important;' +
        'border:1px solid Highlight !important;' +
        'forced-color-adjust:none;' +
      '}' +
    '}';
  document.head.appendChild(style);
})();

/* ShareIconButton — 44x44 hit, 16x16 visual, stopPropagation on click.
 * Renders to the right of SlidingChip with an 8px gap.
 *
 * Click opens the full ShareCardPopover (lib/share-card.js renders a branded
 * PNG of the show; the panel offers native file-share → IG/TikTok/Stories,
 * save, copy, and X/Reddit/Telegram/Facebook deep links). If the popover
 * bundle hasn't loaded (first-paint race / no-JS-component build), we fall
 * back to the legacy native share sheet via window.shareEvent. */
function ShareIconButton({ event, darkMode }) {
  const [shareRect, setShareRect] = React.useState(null);
  if (!event || !event.slug) return null;

  const handleClick = (e) => {
    // CRITICAL — without this, tap also fires the card's tap-to-slide handler
    // which would mark interest + open the ticket link in a new tab.
    e.stopPropagation();
    e.preventDefault();
    // Rich panel when available — capture the icon's rect so the popover can
    // FLIP-morph out of it (same pattern as the MERCH chip).
    if (typeof window !== 'undefined' && typeof window.ShareCardPopover === 'function') {
      setShareRect(e.currentTarget.getBoundingClientRect());
      return;
    }
    // Fallback — legacy native share sheet / clipboard.
    if (typeof window !== 'undefined' && typeof window.shareEvent === 'function') {
      window.shareEvent({
        title: event.title,
        slug: event.slug,
        venue: event.venue,
        date: event.date,
        toast: (msg) => {
          if (typeof window.onsumnToast === 'function') window.onsumnToast(msg);
        },
      });
    }
  };

  const ShareCardPopover = (typeof window !== 'undefined') ? window.ShareCardPopover : null;

  return (
    <React.Fragment>
      <button
        type="button"
        className="onsumn-share-btn"
        onClick={handleClick}
        aria-label={`Share ${event.title}`}
        aria-haspopup="dialog"
        title="Share this show"
        style={{
          // Labeled pill (was icon-only — too easy to miss). Icon + SHARE
          // text, sized to sit alongside the MERCH chip / sliding count in
          // the card's action row. ≥40px hit via padding + min-height.
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: darkMode ? 'oklch(0.80 0.05 70)' : 'oklch(0.34 0.04 250)',
          background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)',
          border: darkMode ? '1px solid rgba(255,255,255,0.20)' : '1px solid rgba(0,0,0,0.16)',
          padding: '6px 11px', borderRadius: 100,
          lineHeight: 1.2, minHeight: 30, cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span aria-hidden="true" style={{ display: 'inline-flex', transform: 'scale(0.82)', transformOrigin: 'center' }}>
          <ShareIcon darkMode={darkMode} />
        </span>
        SHARE
      </button>
      {shareRect && typeof ShareCardPopover === 'function' && (
        <ShareCardPopover
          event={event}
          darkMode={darkMode}
          triggerRect={shareRect}
          onClose={() => setShareRect(null)}
        />
      )}
    </React.Fragment>
  );
}

// Explicit window exports — defensive belt for any consumer that prefers
// window.X over the implicit global. Matches the export pattern at the
// bottom of Events.jsx.
window.markInterested = markInterested;
window.fetchInterestCounts = fetchInterestCounts;
window.useInterestCount = useInterestCount;
window.useHydrateInterest = useHydrateInterest;
window.SlidingChip = SlidingChip;
window.ShareIconButton = ShareIconButton;
window.ShareIcon = ShareIcon;
// Cache lifecycle, exposed so tests/interest-cache-lifecycle.test.js can drive
// eviction directly rather than inferring it from render side effects.
window.__interestCache = {
  read: _readCachedCount,
  write: _writeCachedCount,
  sweep: _sweepInterestCache,
  TTL_MS: _INTEREST_CACHE_TTL_MS,
  MAX_ENTRIES: _INTEREST_CACHE_MAX_ENTRIES,
  PREFIX: _INTEREST_CACHE_KEY_PREFIX,
};
